import React, { useState } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Alert,
  Snackbar,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
} from "@mui/material";
import { submitDeleteAccountRequest } from "../firebase/firebase";

export default function DeleteAccount() {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate email
    if (!email) {
      setEmailError("Email is required");
      return;
    }

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    } else {
      setEmailError("");
    }

    // Get the final reason (either selected or custom)
    const finalReason = reason === "other" ? customReason : reason;

    if (!finalReason) {
      return;
    }

    setLoading(true);

    try {
      const result = await submitDeleteAccountRequest(email, finalReason);

      if (result.success) {
        setSnackbar({
          open: true,
          message:
            "Your account deletion request has been submitted. We will process it within 7 business days.",
          severity: "success",
        });

        // Reset form
        setEmail("");
        setReason("");
        setCustomReason("");
      } else {
        setSnackbar({
          open: true,
          message:
            "There was an error submitting your request. Please try again.",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          "There was an error submitting your request. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Request Account Deletion
        </Typography>

        <Typography variant="body1" paragraph>
          As per our privacy policy, you can request to delete your account and
          all associated data. Please fill out the form below and we will
          process your request within 7 business days.
        </Typography>

        <Typography variant="body2" paragraph sx={{ color: "text.secondary" }}>
          Note: This action cannot be undone. All your data, including
          favorites, progress, and personal information will be permanently
          deleted.
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 4 }}>
          <TextField
            fullWidth
            label="Email Address"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!emailError}
            helperText={emailError}
            margin="normal"
            required
            type="email"
          />

          <FormControl fullWidth margin="normal" required>
            <InputLabel id="reason-label">Reason for Deletion</InputLabel>
            <Select
              labelId="reason-label"
              value={reason}
              label="Reason for Deletion"
              onChange={(e) => setReason(e.target.value)}
            >
              <MenuItem value="">
                <em>Select a reason</em>
              </MenuItem>
              <MenuItem value="no_longer_use">I no longer use the app</MenuItem>
              <MenuItem value="privacy_concerns">Privacy concerns</MenuItem>
              <MenuItem value="not_useful">
                The app is not useful to me
              </MenuItem>
              <MenuItem value="too_many_accounts">
                I have too many accounts
              </MenuItem>
              <MenuItem value="other">Other reason</MenuItem>
            </Select>
            <FormHelperText>
              Please select a reason for deleting your account
            </FormHelperText>
          </FormControl>

          {reason === "other" && (
            <TextField
              fullWidth
              label="Please specify"
              variant="outlined"
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              margin="normal"
              required
              multiline
              rows={3}
            />
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={loading}
            sx={{ mt: 3 }}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              "Submit Deletion Request"
            )}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
